import { Realtime, RealtimeChannel } from 'ably';
import { backOff } from 'exponential-backoff';
import React, { FC, useCallback, useEffect, useRef, useState } from 'react';

import { useOnChannelMessage } from '../hooks/use-message-logging';
import { useMessagingDeps } from '../provider/use-messaging-deps';
import { authenticate } from '../queries/auth';
import { useMessagingConfigQuery } from '../queries/config';
import { MessagingChannelType, MessagingProvider, MessagingProviderName } from '../types/messaging';

export function useAblyAuth(
  channelType: MessagingChannelType,
  setForceKillAbly: (isUnauthorized: boolean) => void,
  registerId?: number,
) {
  // transport/logger/metrics come from the DI seam (was register's `post`/logger/useMetrics).
  const { transport, logger, metrics } = useMessagingDeps();

  const auth = useCallback(() => {
    return new Realtime({
      authCallback: async (tokenParams, callback) => {
        let tokenRequest: string;
        try {
          tokenRequest = await backOff<string>(
            // authenticate will call our endpoint that returns a token request that ably uses to authenticate client-side
            // Ably runs this internally, if success, we call their callback to authenticate (line 61)
            async () =>
              await authenticate<string>(transport, {
                Provider: MessagingProvider.Ably,
                ChannelType: channelType,
                RegisterId: registerId,
              }),
            {
              numOfAttempts: 3,
              retry: (err, attemptNumber) => {
                // Log each failed attempt. Do NOT call callback here — Ably's authCallback
                // contract requires exactly one invocation per auth cycle. Calling it on
                // every retry causes duplicate error state transitions in the Ably client.
                metrics.addCount('ably.auth-failure');
                logger.error(`${MessagingProviderName.Ably} authentication failed.  Attempt number ${attemptNumber}.`, {
                  Exception: err,
                  ChannelType: channelType,
                });
                return true;
              },
            },
          );
        } catch (err: any) {
          // We should reach this block once we exhaust our 3 allowed attempts
          // at this point, we will log again and then force kill the ably client/channel connection
          metrics.addCount('ably.auth-failure-max');
          logger.error(`${MessagingProviderName.Ably} authentication failed.  Reached max number of attempts.`, {
            Exception: err,
            ChannelType: channelType,
          });

          // if we've reached this block it seems safe to assume that token request is undefined
          // for whatever reason (I think logging), ably indicates we should call this on error as well as success
          callback(err, '');

          setForceKillAbly(true);

          return;
        }

        callback('', JSON.parse(tokenRequest));
      },
    });
  }, [channelType, transport, logger, metrics, registerId, setForceKillAbly]);

  return auth;
}

export const useAblyChannel = (
  channelType: MessagingChannelType,
  channelMessageCallback: (eventName: string, data: string | object) => void,
  registerId?: number,
) => {
  // Ensuring client and channel are saved as refs rather than state st they can be detached/closed/unsubscribed on dismount
  const clientRef = useRef<Realtime>();
  const channelRef = useRef<RealtimeChannel>();
  const onChannelMessage = useOnChannelMessage(channelMessageCallback, MessagingProviderName.Ably);

  // The Ably channel is subscribed only once (guarded by clientRef below), so capturing
  // onChannelMessage directly in the subscribe closure would freeze it at first-subscribe —
  // which happens on mount while the QR screen is still closed (enabled=false) — and then
  // drop every later event. Keep the latest handler in a ref and call ref.current, mirroring
  // the ref pattern in usePusherChannel.
  const onChannelMessageRef = useRef(onChannelMessage);
  onChannelMessageRef.current = onChannelMessage;

  const [forceKillAbly, setForceKillAbly] = useState<boolean>(false);
  const auth = useAblyAuth(channelType, setForceKillAbly, registerId);
  const { data: config } = useMessagingConfigQuery({
    Provider: MessagingProvider.Ably,
    ChannelType: channelType,
    RegisterId: registerId,
  });

  useEffect(() => {
    if (config && !clientRef.current && !channelRef.current && !forceKillAbly) {
      const client = auth();

      // create ref st client can be closed on dismount
      clientRef.current = client;

      const channel = client.channels.get(config.ChannelName);
      channel.subscribe((message) => onChannelMessageRef.current(message.name, message.data));

      // create ref st client can be closed on dismount
      channelRef.current = channel;
    }
    // onChannelMessage is intentionally omitted: it's invoked via onChannelMessageRef so a
    // changed handler doesn't need to (and, given the clientRef guard, can't) re-subscribe.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [auth, config, forceKillAbly]);

  // Upon 401 authentication response, kill any active client/channels such that authentication retries stop
  useEffect(() => {
    if (forceKillAbly) {
      channelRef.current?.detach();
      channelRef.current?.unsubscribe();
      clientRef.current?.close();
    }
  }, [forceKillAbly]);

  useEffect(() => {
    return () => {
      channelRef.current?.detach();
      channelRef.current?.unsubscribe();
      clientRef.current?.close();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
};

export type AblyChannelProps = {
  channelType: MessagingChannelType;
  channelMessageCallback: (eventName: string, data: string | object) => void;
  registerId?: number;
};

export const AblyChannel: FC<AblyChannelProps> = ({ channelType, channelMessageCallback, registerId }) => {
  useAblyChannel(channelType, channelMessageCallback, registerId);
  return <></>;
};
